/**
 * @class
 * Example use:
 * 	var resizeDebouncer = new Debouncer(callbackFn);
 *  window.addEventListener('resize', resizeDebouncer.run, false);
 *  
 * Or, if using JQuery (but you can also 
 * 	var resizeDebouncer = new Debouncer(callbackFn);
 *  $(window).bind('resize', resizeDebouncer.run);
 * 
 * Or, all on one line:
 *  $(window).bind('resize', (new Debouncer(callbackFn)).run);
 *  
 */
export default class Debouncer {
	private waitingID: number;
	private immediateSkipped: boolean = undefined;

	private callbackThis: any;
	private callbackArgs: any[];

	/**
	 * 
	 * @param {function} callback 
	 * @param {boolean} [immediate=false] - If `immediate` is set to true, the callback function is called on the leading edge and at the end of the interval if an event happens since first event but not during waiting period. If false, called at the end of each interval and not at the start.
	 * @param {number} [timeout] - The interval in ms to use to debounce. If not specified, the debouncer uses requestAnimationFrame.
	 */
	constructor(
		private callback: (this: any, ...args:any[]) => void,
		private immediate:boolean = false,
		private timeout?:number
	) {
		const self = this;
		this.run = function(...args:any[]) {
			self._run(this, args);
		};

		this.laterFn = this.laterFn.bind(this);
	}

	/**
	 * This method is commonly used as a callback. When used 'this' may reference a different object. This method
	 * will ensure the 'this' is used as the 'this' used when the callback is executed.
	 */
	run(...args:any[]): void {
		// Mehod replaced in constructor
	}

	private _run(thisArg:any, args:any[]): void {
		this.callbackThis = thisArg;
		this.callbackArgs = args;

		if (this.immediate === true) {
			if (this.waitingID === undefined) {
				// Call now (if set to immediate and not waiting).
				this.immediateSkipped = false;
				this.callback.apply(this.callbackThis, this.callbackArgs);
			} else {
				this.immediateSkipped = true;
			}
		}

		if (this.waitingID === undefined) {
			if (this.timeout === undefined) {
				this.waitingID = window.requestAnimationFrame(this.laterFn);
			} else {
				this.waitingID = window.setTimeout(this.laterFn, this.timeout);
			}
		}
	}

	private laterFn() {
		this.waitingID = undefined;
		if (this.immediate !== true || this.immediateSkipped === true) {
			this.immediateSkipped = undefined;
			this.callback.apply(this.callbackThis, this.callbackArgs);
		}
	}

	/**
	 * Run the callback method immediately without debouncing.
	 * Any active debouncing is canceled first.
	 * @public
	 */
	runNow(...args: any[]) {
		this.cancel();
		this.callback.apply(this, args);
	}

	/**
	 * Cancel a call to 'run'.
	 * @public
	 */
	cancel() {
		if (this.waitingID !== undefined) {
			if (this.timeout === undefined) {
				window.cancelAnimationFrame(this.waitingID);
			} else {
				window.clearTimeout(this.waitingID);
			}

			this.waitingID = undefined;
		}
	}

}